// Design tokens. Everything visual should come from here so the whole site
// stays on one palette, one spacing scale, and two radii.

export const font = {
  mono: '"Courier Prime", "SFMono-Regular", Menlo, monospace',
  // Editorial serif for prose — deliberately not a neutral grotesque, so the
  // second face reads as a voice rather than a system default.
  body: '"Newsreader", "Iowan Old Style", "Palatino Linotype", Georgia, serif'
};

export const colors = {
  // grounds
  bg: '#FAF8F3',
  surface: '#FFFDF8',
  surfaceHover: '#F9F6EF',

  // ink ramp — warm, so nothing reads as pure black on cream
  inkStrong: '#282A24',
  ink: '#33352F',
  muted: '#5C6157',

  // sage accents
  sage: '#8B9382',
  sageLight: '#A5B199',
  sageDeep: '#6B7366',

  // hairlines replace every drop shadow
  hairline: 'rgba(139, 147, 130, 0.22)',
  hairlineStrong: 'rgba(139, 147, 130, 0.45)',

  highlight: 'rgba(139, 147, 130, 0.20)'
};

// 8px scale (with a 4px half-step for tight pairings)
export const space = {
  half: '4px',
  1: '8px',
  2: '16px',
  3: '24px',
  4: '32px',
  5: '40px',
  6: '48px',
  7: '64px',
  8: '80px'
};

// Two radii. Nothing else.
export const radius = {
  sm: '4px',
  md: '10px',
  round: '50%'
};

export const layout = {
  page: '940px', // card and timeline pages
  prose: '760px' // long-form measure — ~80 characters at the 18px serif
};

export const motion = {
  // gentle out-curve — the old ease was snappier than this palette wants
  ease: 'cubic-bezier(0.32, 0.72, 0, 1)',
  fast: '0.25s',
  slow: '0.5s'
};

// The matte card: flat surface, hairline border, no shadow, no lift.
export const card = {
  background: colors.surface,
  border: `1px solid ${colors.hairline}`,
  borderRadius: radius.md,
  transition: `background ${motion.fast} ease, border-color ${motion.fast} ease`
};

export const cardHover = {
  background: colors.surfaceHover,
  borderColor: colors.hairlineStrong
};

// Type ramp. Headings and data stay mono; prose is sans.
export const type = {
  h1: (isMobile) => ({
    fontFamily: font.mono,
    fontSize: isMobile ? '32px' : '44px',
    fontWeight: 700,
    letterSpacing: '-1px',
    lineHeight: 1.15,
    color: colors.inkStrong,
    margin: 0
  }),
  h2: {
    fontFamily: font.mono,
    fontSize: '24px',
    fontWeight: 700,
    letterSpacing: '-0.5px',
    color: colors.sageDeep
  },
  h3: {
    fontFamily: font.mono,
    fontSize: '20px',
    fontWeight: 700,
    letterSpacing: '-0.3px',
    color: colors.inkStrong,
    margin: 0
  },
  subtitle: {
    fontFamily: font.mono,
    fontSize: '17px',
    color: colors.sage,
    fontStyle: 'italic'
  },
  body: {
    fontFamily: font.body,
    fontSize: '18px',
    lineHeight: 1.7,
    color: colors.ink
  },
  // small mono for tech tags, dates, meta labels — the "data" voice
  meta: {
    fontFamily: font.mono,
    fontSize: '13px',
    color: colors.sage,
    letterSpacing: '0.2px'
  },
  label: {
    fontFamily: font.mono,
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '1.5px',
    color: colors.sageDeep
  },
  link: {
    fontFamily: font.mono,
    fontSize: '14px',
    color: colors.sageDeep,
    fontStyle: 'italic',
    textDecoration: 'none'
  }
};

// Marker underlay. The stop is tuned to the serif's x-height — retune it if
// the body face ever changes.
export const highlight = {
  background: `linear-gradient(180deg, transparent 52%, ${colors.highlight} 52%)`,
  padding: '0 2px'
};
